import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Container, Row, Col, Card, CardBody,
  Table, Badge, Button, Input, InputGroup, Spinner,
  Modal, ModalHeader, ModalBody, ModalFooter,
  Nav, NavItem, NavLink, TabContent, TabPane,
  Alert, Pagination, PaginationItem, PaginationLink,
} from "reactstrap";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import { useAuthUrl } from "../utils/authRedirect";
import requests from "../requests";
import { useSeo } from "../hooks/useSeo";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface AdminUser {
  sub: string;
  email: string;
  username: string;
  created: string | null;
  enabled: boolean;
  status: string;
  is_pro: boolean;
  is_admin: boolean;
  alert_count?: number;
  watchlist_count?: number;
  total_queries?: number;
  stripe_customer_id?: string;
  /** queries + alerts + watchlist entries. Zero is the bot signature. */
  engagement?: number;
  /** Past the grace period having done none of the three. Evidence, not a verdict. */
  is_inert?: boolean;
}

/** Whole-pool counts, computed server-side BEFORE filtering so they don't move as you narrow. */
interface AdminSummary {
  total: number;
  enabled: number;
  disabled: number;
  inert: number;
  inert_enabled: number;
  grace_days: number;
}

interface UserDetail {
  profile: AdminUser & { groups: string[]; last_modified: string | null };
  alerts: { id: string; name: string; filters: unknown; created_at: string | null }[];
  watchlist: { ticker: string; ticker_name: string; created_at: string }[];
  query_usage: { total: number; recent: { date: string; count: number }[] };
  billing: { stripe_customer_id: string | null };
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
function TierBadge({ is_pro, is_admin }: { is_pro: boolean; is_admin: boolean }) {
  if (is_admin) return <Badge color="danger">Admin</Badge>;
  if (is_pro) return <Badge color="warning" text="dark">Pro</Badge>;
  return <Badge color="secondary">Free</Badge>;
}

function UserDetailModal({ sub, onClose }: { sub: string; onClose: () => void }) {
  const [detail, setDetail] = useState<UserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("profile");
  const [actionMsg, setActionMsg] = useState<{ color: string; text: string } | null>(null);

  useEffect(() => {
    requests.get(`/api/admin/users/${sub}`)
      .then(r => { setDetail(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [sub]);

  const action = useCallback(async (method: "post" | "delete", path: string, label: string) => {
    setActionMsg(null);
    try {
      await requests[method](path);
      setActionMsg({ color: "success", text: `${label} — done` });
      const r = await requests.get(`/api/admin/users/${sub}`);
      setDetail(r.data);
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: string } } } | undefined)?.response?.data?.error;
      setActionMsg({ color: "danger", text: msg ?? `${label} failed` });
    }
  }, [sub]);

  const deleteAlert = (alertId: string) =>
    void action("delete", `/api/admin/users/${sub}/alerts/${alertId}`, "Alert deleted");
  const deleteWatchItem = (ticker: string) =>
    void action("delete", `/api/admin/users/${sub}/watchlist/${ticker}`, `${ticker} removed`);
  const grantPro = () => void action("post", `/api/admin/users/${sub}/pro`, "Pro granted");
  const revokePro = () => void action("delete", `/api/admin/users/${sub}/pro`, "Pro revoked");
  const sendDigest = () => void action("post", `/api/admin/users/${sub}/digest`, "Digest sent");

  return (
    <Modal isOpen size="xl" toggle={onClose} style={{ maxWidth: 900 }}>
      <ModalHeader toggle={onClose}>
        {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- detail can be {} (malformed body) so profile may be absent */}
        {detail?.profile?.email ?? sub}
        {detail?.profile && <span className="ms-2"><TierBadge is_pro={detail.profile.is_pro} is_admin={detail.profile.is_admin} /></span>}
      </ModalHeader>
      <ModalBody style={{ minHeight: 400 }}>
        {loading ? (
          <div className="text-center py-5"><Spinner color="primary" /></div>
        ) : !detail?.profile ? (
          <Alert color="danger">Failed to load user detail.</Alert>
        ) : (
          <>
            {actionMsg && (
              <Alert color={actionMsg.color} className="mb-3">{actionMsg.text}</Alert>
            )}

            {/* Action bar */}
            <div className="d-flex gap-2 mb-3 flex-wrap">
              {detail.profile.is_pro ? (
                <Button size="sm" color="warning" onClick={revokePro}>Revoke Pro</Button>
              ) : (
                <Button size="sm" color="success" onClick={grantPro}>Grant Pro</Button>
              )}
              <Button size="sm" color="info" onClick={sendDigest}>Send Digest</Button>
            </div>

            <Nav tabs className="mb-3">
              {["profile", "alerts", "watchlist", "usage", "billing"].map(t => (
                <NavItem key={t}>
                  <NavLink
                    active={activeTab === t}
                    onClick={() => setActiveTab(t)}
                    style={{ cursor: "pointer", textTransform: "capitalize" }}
                  >
                    {t}
                    {t === "alerts" && ` (${detail.alerts.length})`}
                    {t === "watchlist" && ` (${detail.watchlist.length})`}
                  </NavLink>
                </NavItem>
              ))}
            </Nav>

            <TabContent activeTab={activeTab}>
              {/* Profile */}
              <TabPane tabId="profile">
                <table className="table table-sm">
                  <tbody>
                    {([
                      ["Sub", detail.profile.sub],
                      ["Email", detail.profile.email],
                      ["Username", detail.profile.username],
                      ["Joined", detail.profile.created],
                      ["Last Modified", detail.profile.last_modified],
                      ["Status", detail.profile.status],
                      ["Groups", detail.profile.groups.join(", ") || "none"],
                    ] as [string, string | null][]).map(([k, v]) => (
                      <tr key={k}>
                        <td className="text-muted" style={{ width: 160 }}>{k}</td>
                        <td>{v ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TabPane>

              {/* Alerts */}
              <TabPane tabId="alerts">
                {detail.alerts.length === 0 ? (
                  <p className="text-muted">No alerts.</p>
                ) : (
                  <Table size="sm" responsive hover>
                    <thead><tr><th>Name</th><th>Created</th><th>Filters</th><th></th></tr></thead>
                    <tbody>
                      {detail.alerts.map(a => (
                        <tr key={a.id}>
                          <td>{a.name}</td>
                          <td className="text-muted" style={{ fontSize: 12 }}>{a.created_at}</td>
                          <td className="text-muted" style={{ fontSize: 11, maxWidth: 300, wordBreak: "break-all" }}>
                            {JSON.stringify(a.filters).slice(0, 120)}…
                          </td>
                          <td>
                            <Button size="sm" color="link" style={{ color: "var(--error)" }}
                              onClick={() => deleteAlert(a.id)}>Delete</Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </TabPane>

              {/* Watchlist */}
              <TabPane tabId="watchlist">
                {detail.watchlist.length === 0 ? (
                  <p className="text-muted">No watchlist items.</p>
                ) : (
                  <Table size="sm" responsive hover>
                    <thead><tr><th>Ticker</th><th>Name</th><th>Added</th><th></th></tr></thead>
                    <tbody>
                      {detail.watchlist.map(w => (
                        <tr key={w.ticker}>
                          <td style={{ fontWeight: 600 }}>{w.ticker}</td>
                          <td className="text-muted">{w.ticker_name}</td>
                          <td className="text-muted" style={{ fontSize: 12 }}>{w.created_at}</td>
                          <td>
                            <Button size="sm" color="link" style={{ color: "var(--error)" }}
                              onClick={() => deleteWatchItem(w.ticker)}>Delete</Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </TabPane>

              {/* Usage */}
              <TabPane tabId="usage">
                <p className="text-muted">Total queries (all-time): <strong>{detail.query_usage.total}</strong></p>
                <Table size="sm" responsive>
                  <thead><tr><th>Date</th><th>Queries</th></tr></thead>
                  <tbody>
                    {detail.query_usage.recent.map(r => (
                      <tr key={r.date}>
                        <td className="text-muted">{r.date}</td>
                        <td>{r.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TabPane>

              {/* Billing */}
              <TabPane tabId="billing">
                <table className="table table-sm">
                  <tbody>
                    <tr>
                      <td className="text-muted">Stripe Customer ID</td>
                      <td>{detail.billing.stripe_customer_id ?? <span className="text-muted">none</span>}</td>
                    </tr>
                    <tr>
                      <td className="text-muted">Tier</td>
                      <td><TierBadge is_pro={detail.profile.is_pro} is_admin={detail.profile.is_admin} /></td>
                    </tr>
                  </tbody>
                </table>
              </TabPane>
            </TabContent>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onClose}>Close</Button>
      </ModalFooter>
    </Modal>
  );
}

type SortField =
  | "email"
  | "tier"
  | "created"
  | "alert_count"
  | "watchlist_count"
  | "total_queries"
  | "engagement"
  | "stripe_customer_id";

const SORTABLE_COLUMNS: { key: SortField; label: string }[] = [
  { key: "email", label: "Email" },
  { key: "tier", label: "Tier" },
  { key: "created", label: "Joined" },
  { key: "alert_count", label: "Alerts" },
  { key: "watchlist_count", label: "Watchlist" },
  { key: "total_queries", label: "Queries" },
  { key: "engagement", label: "Activity" },
  { key: "stripe_customer_id", label: "Stripe" },
];

// ---------------------------------------------------------------------------
// Main admin dashboard
// ---------------------------------------------------------------------------
export default function AdminDashboard() {
  useSeo({ title: "Admin", path: "/admin", noindex: true });
  const authUrlFor = useAuthUrl();
  const loggedIn = useSelector((s: RootReducerState) => s.authentication.loggedIn as boolean);
  const authChecked = useSelector((s: RootReducerState) => s.authentication.authChecked as boolean);
  const user = useSelector((s: RootReducerState) => s.authentication.user);
  const groups = useMemo<string[]>(() => (user as { groups?: string[] } | null)?.groups ?? [], [user]);

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState("");
  const [tier, setTier] = useState("");
  // Default to enabled-only: after the September quarantine the pool is 14 real accounts
  // and 195 disabled bots, so an unfiltered list is 93% noise.
  const [status, setStatus] = useState("enabled");
  const [activity, setActivity] = useState("");
  const [summary, setSummary] = useState<AdminSummary | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState<{ color: string; text: string } | null>(null);
  const [sortBy, setSortBy] = useState<SortField>("created");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [loading, setLoading] = useState(false);
  const [selectedSub, setSelectedSub] = useState<string | null>(null);

  const fetchUsers = useCallback((p = 1, q = search) => {
    setLoading(true);
    requests.get("/api/admin/users", {
      params: {
        page: p,
        per_page: 50,
        search: q,
        tier: tier || undefined,
        status: status || undefined,
        activity: activity || undefined,
        sort_by: sortBy,
        sort_dir: sortDir,
      },
    })
      .then(r => {
        setUsers(Array.isArray(r.data?.users) ? r.data.users : []);
        setTotal(r.data?.total ?? 0);
        setPage(r.data?.page ?? 1);
        setPages(r.data?.pages ?? 1);
        setSummary(r.data?.summary ?? null);
        // Drop the selection on every refetch. Keeping it would let a filter change carry
        // invisible off-screen rows into a bulk disable — the selection must never contain
        // anything the admin cannot see.
        setSelected(new Set());
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [search, tier, status, activity, sortBy, sortDir]);

  const toggleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortDir(d => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortDir("asc");
    }
  };

  useEffect(() => {
    if (authChecked && loggedIn && groups.includes("admin")) fetchUsers();
  }, [authChecked, loggedIn, groups, fetchUsers]);

  if (!authChecked) return <Container className="py-5 text-center"><Spinner color="primary" /></Container>;
  if (!loggedIn) return <Navigate to={authUrlFor("/login")} replace />;
  if (!groups.includes("admin")) return (
    <Container className="py-5 text-center">
      <p className="text-muted">You don't have admin access.</p>
    </Container>
  );

  const submitSearch = () => fetchUsers(1, search);

  const toggleOne = (sub: string) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(sub)) next.delete(sub); else next.add(sub);
    return next;
  });

  // Selects only what is on THIS page — never the whole filtered set. The bulk endpoint
  // takes an explicit list of subs rather than a filter for the same reason: the blast
  // radius should always be exactly what the admin looked at.
  const selectablePage = users.filter(u => !u.is_pro && !u.is_admin && u.enabled);
  const allPageSelected = selectablePage.length > 0 && selectablePage.every(u => selected.has(u.sub));
  const togglePage = () => setSelected(prev => {
    const next = new Set(prev);
    if (allPageSelected) selectablePage.forEach(u => next.delete(u.sub));
    else selectablePage.forEach(u => next.add(u.sub));
    return next;
  });

  const setEnabled = (sub: string, enabled: boolean) => {
    setBusy(true);
    const req = enabled
      ? requests.post(`/api/admin/users/${sub}/enabled`)
      : requests.delete(`/api/admin/users/${sub}/enabled`);
    req
      .then(() => { setBanner({ color: "success", text: `Account ${enabled ? "re-enabled" : "disabled"}.` }); fetchUsers(page); })
      .catch(e => setBanner({ color: "danger", text: e?.response?.data?.error ?? "Request failed." }))
      .finally(() => setBusy(false));
  };

  const bulkDisable = () => {
    const subs = [...selected];
    if (!subs.length) return;
    if (!window.confirm(
      `Disable ${subs.length} account${subs.length === 1 ? "" : "s"}?\n\n` +
      `They stop receiving the daily digest immediately. Nothing is deleted, and you can ` +
      `re-enable any of them from the Disabled filter.`
    )) return;
    setBusy(true);
    requests.post("/api/admin/users/disable", { subs })
      .then(r => {
        const failed = Object.keys(r.data?.failed ?? {}).length;
        setBanner({
          color: failed ? "warning" : "success",
          text: `Disabled ${r.data?.disabled_count ?? 0} account(s)` + (failed ? `; ${failed} failed.` : "."),
        });
        fetchUsers(page);
      })
      .catch(e => {
        const d = e?.response?.data;
        setBanner({
          color: "danger",
          text: d?.protected_subs?.length
            ? `${d.error} (${d.protected_subs.length} protected account(s) in the selection.)`
            : (d?.error ?? "Bulk disable failed."),
        });
      })
      .finally(() => setBusy(false));
  };

  return (
    <Container fluid className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-1">
        <h2 className="mb-0" style={{ fontWeight: 700 }}>Admin Dashboard</h2>
      </div>
      <p className="text-muted mb-3" style={{ fontSize: 14 }}>
        {total} shown
        {summary && (
          <>
            {" · "}{summary.enabled} enabled · {summary.disabled} disabled
            {summary.inert_enabled > 0 && (
              <>
                {" · "}
                <span style={{ color: "var(--warning, #b8860b)" }}>
                  {summary.inert_enabled} enabled but inactive
                </span>
              </>
            )}
          </>
        )}
      </p>

      {banner && (
        <Alert color={banner.color} toggle={() => setBanner(null)} className="py-2">
          {banner.text}
        </Alert>
      )}

      {/* Search + filters */}
      <Row className="mb-2 g-2">
        <Col xs={12} md={4}>
          <InputGroup>
            <Input
              placeholder="Search by email or username"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === "Enter" && submitSearch()}
            />
            <Button color="primary" onClick={submitSearch}>Search</Button>
          </InputGroup>
        </Col>
        <Col xs={6} md={2}>
          <Input type="select" value={tier} onChange={e => setTier(e.target.value)}>
            <option value="">All tiers</option>
            <option value="free">Free</option>
            <option value="pro">Pro</option>
            <option value="admin">Admin</option>
          </Input>
        </Col>
        <Col xs={6} md={2}>
          <Input type="select" value={status} onChange={e => setStatus(e.target.value)}
            title="Disabled accounts receive no email.">
            <option value="enabled">Enabled only</option>
            <option value="disabled">Disabled only</option>
            <option value="">Enabled + disabled</option>
          </Input>
        </Col>
        <Col xs={6} md={3}>
          <Input type="select" value={activity} onChange={e => setActivity(e.target.value)}
            title="Inactive = no queries, alerts or watchlist entries after the grace period.">
            <option value="">Any activity</option>
            <option value="inert">
              Never used the product{summary ? ` (${summary.grace_days}+ days old)` : ""}
            </option>
            <option value="active">Has used the product</option>
          </Input>
        </Col>
      </Row>

      {/* Bulk action bar — only when something is selected, so it is never ambient. */}
      {selected.size > 0 && (
        <div className="d-flex align-items-center gap-2 mb-3 p-2 rounded"
          style={{ background: "var(--bs-secondary-bg, #f1f3f5)" }}>
          <span style={{ fontSize: 13 }}>{selected.size} selected on this page</span>
          <Button size="sm" color="danger" outline disabled={busy} onClick={bulkDisable}>
            Disable selected
          </Button>
          <Button size="sm" color="link" onClick={() => setSelected(new Set())}>Clear</Button>
          <span className="text-muted ms-auto" style={{ fontSize: 12 }}>
            Disabling stops the daily digest. Nothing is deleted — reversible from “Disabled only”.
          </span>
        </div>
      )}

      <Card>
        <CardBody className="p-0">
          {loading ? (
            <div className="text-center py-4"><Spinner color="primary" /></div>
          ) : (
            <Table responsive hover className="mb-0" style={{ fontSize: 13 }}>
              <thead>
                <tr className="text-muted">
                  <th style={{ width: 32 }}>
                    <Input type="checkbox" checked={allPageSelected}
                      disabled={selectablePage.length === 0}
                      onChange={togglePage}
                      title="Select every eligible account on this page"
                      aria-label="Select all on this page" />
                  </th>
                  {SORTABLE_COLUMNS.map(col => (
                    <th
                      key={col.key}
                      onClick={() => toggleSort(col.key)}
                      style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}
                    >
                      {col.label}
                      {sortBy === col.key && (sortDir === "asc" ? " ▲" : " ▼")}
                    </th>
                  ))}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.sub} style={{ cursor: "pointer", opacity: u.enabled ? 1 : 0.55 }}
                    onClick={() => setSelectedSub(u.sub)}>
                    <td onClick={e => e.stopPropagation()}>
                      <Input type="checkbox"
                        checked={selected.has(u.sub)}
                        disabled={u.is_pro || u.is_admin || !u.enabled}
                        onChange={() => toggleOne(u.sub)}
                        aria-label={`Select ${u.email}`} />
                    </td>
                    <td>
                      <span style={{ fontWeight: 500 }}>{u.email}</span>
                      {!u.enabled && <Badge color="danger" className="ms-1" style={{ fontSize: 10 }}>disabled</Badge>}
                      {u.enabled && u.is_inert && (
                        <Badge color="warning" text="dark" className="ms-1" style={{ fontSize: 10 }}
                          title="No queries, alerts or watchlist entries since signing up. Evidence, not a verdict.">
                          inactive
                        </Badge>
                      )}
                    </td>
                    <td><TierBadge is_pro={u.is_pro} is_admin={u.is_admin} /></td>
                    <td className="text-muted">{u.created}</td>
                    <td className="text-muted">{u.alert_count ?? 0}</td>
                    <td className="text-muted">{u.watchlist_count ?? 0}</td>
                    <td className="text-muted">{u.total_queries ?? 0}</td>
                    <td className={u.engagement ? "" : "text-muted"}
                      style={{ fontWeight: u.engagement ? 600 : 400 }}>
                      {u.engagement ?? 0}
                    </td>
                    <td className="text-muted" style={{ fontSize: 11 }}>
                      {u.stripe_customer_id ? "✓" : "—"}
                    </td>
                    <td onClick={e => e.stopPropagation()} style={{ whiteSpace: "nowrap" }}>
                      <Button size="sm" color="link" style={{ padding: 0 }}
                        onClick={() => setSelectedSub(u.sub)}>
                        View
                      </Button>
                      {!u.is_pro && !u.is_admin && (
                        <Button size="sm" color="link" disabled={busy}
                          style={{ padding: 0, marginLeft: 10 }}
                          onClick={() => setEnabled(u.sub, !u.enabled)}>
                          {u.enabled ? "Disable" : "Enable"}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      {/* Pagination */}
      {pages > 1 && (
        <Pagination className="mt-3 justify-content-center">
          <PaginationItem disabled={page === 1}>
            <PaginationLink previous onClick={() => fetchUsers(page - 1)} />
          </PaginationItem>
          {Array.from({ length: pages }, (_, i) => i + 1).map(p => (
            <PaginationItem active={p === page} key={p}>
              <PaginationLink onClick={() => fetchUsers(p)}>{p}</PaginationLink>
            </PaginationItem>
          ))}
          <PaginationItem disabled={page === pages}>
            <PaginationLink next onClick={() => fetchUsers(page + 1)} />
          </PaginationItem>
        </Pagination>
      )}

      {selectedSub && (
        <UserDetailModal
          sub={selectedSub}
          onClose={() => { setSelectedSub(null); fetchUsers(page); }}
        />
      )}
    </Container>
  );
}
